import { GlobalThemeSteppers } from '@/components/theme/ThemeBar';
import { ThemeModelsGallery } from '@/components/admin/ThemeModelsGallery';
import { useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  coversApi,
  templatesApi,
  settingsApi,
  LEGACY_ALBUM_CATEGORY_TO_SLUG,
  type CoverCategory,
  type LayoutTemplate,
} from '@/lib/api';
import { AdminLayout } from '@/components/AdminLayout';
import { Button } from '@/components/ui/Button';
import { UploadTemplateDialog } from './templates/UploadTemplateDialog';
import { TemplateEditDialog } from './templates/TemplateEditDialog';
import { cn } from '@/lib/utils';
import { useT } from '@/lib/i18n';

// Category chips + filtering now use the unified COVER taxonomy
// (cover_categories, fetched below) — matching the Covers page and the AI
// template matcher. The legacy English enum lists were removed.

export default function AdminLayoutTemplatesPage() {
  const { t } = useT();
  const queryClient = useQueryClient();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editing, setEditing] = useState<LayoutTemplate | null>(null);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>(''); // cover_categories slug
  const [recentFirst, setRecentFirst] = useState(false);
  // Two tabs (owner, 2026-10-14): « Thèmes (v2) » — the design system v2 models,
  // which live in code (default) — and « Anciens modèles » — the Canva templates
  // of layout_templates. ?tab=anciens opens the second.
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: 'themes' | 'anciens' = searchParams.get('tab') === 'anciens' ? 'anciens' : 'themes';
  const setTab = useCallback((v: 'themes' | 'anciens') => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (v === 'anciens') next.set('tab', 'anciens'); else next.delete('tab');
      return next;
    }, { replace: true });
  }, [setSearchParams]);
  const [themeCount, setThemeCount] = useState<{ n: number; theme: string } | null>(null);
  const onThemeCount = useCallback((n: number, theme: string) => setThemeCount({ n, theme }), []);
  // Retired templates (is_visible = false — the 48 old Canva models the owner
  // removed; rows kept because albums still use them) are hidden by default.
  const [showHidden, setShowHidden] = useState(false);

  // Multi-select state (Task 21)
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // Bulk multi-category picker (checkboxes + one Apply).
  const [bulkCatOpen, setBulkCatOpen] = useState(false);
  const [bulkCats, setBulkCats] = useState<Set<string>>(new Set());

  const toggleSelected = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  useEffect(() => {
    document.title = 'Modèles — Admin Storiz';
  }, []);

  // ISSUE 3 (2026-07-05) — template-usage slider: what share of AI-Magic pages
  // use templates. Saved on release (not per-drag) to app_settings.
  const { data: settingsData } = useQuery({ queryKey: ['settings'], queryFn: () => settingsApi.get() });
  const [tplPct, setTplPct] = useState<number | null>(null);
  const effectivePct = tplPct ?? settingsData?.settings?.template_usage_pct ?? 30;
  const [pctSaving, setPctSaving] = useState(false);
  const savePct = useCallback(async (v: number) => {
    setPctSaving(true);
    try {
      await settingsApi.update({ template_usage_pct: v });
      queryClient.invalidateQueries({ queryKey: ['settings'] });
    } catch { /* keep local value; next save retries */ }
    setPctSaving(false);
  }, [queryClient]);

  // Clear selection when exiting select mode
  useEffect(() => {
    if (!selectMode) clearSelection();
  }, [selectMode, clearSelection]);

  // Clear selection when filters/search change so we don't carry stale ids.
  useEffect(() => {
    clearSelection();
  }, [search, categoryFilter, showHidden, tab, clearSelection]);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-templates'],
    queryFn: () => templatesApi.list({ include_hidden: true }),
  });

  // Unified cover taxonomy — used to label templates' album_categories slugs.
  const { data: categoriesData } = useQuery({
    queryKey: ['cover-categories'],
    queryFn: () => coversApi.categories(),
    staleTime: 60_000,
  });
  const categoryMap = useMemo(() => {
    const map: Record<string, CoverCategory> = {};
    for (const c of categoriesData?.categories ?? []) map[c.slug] = c;
    return map;
  }, [categoriesData]);

  const coverCategories = categoriesData?.categories ?? [];

  const allTemplates = data?.templates ?? [];
  const hiddenCount = useMemo(() => allTemplates.filter((x) => !x.is_visible).length, [allTemplates]);
  const templates = useMemo(
    () => (showHidden ? allTemplates : allTemplates.filter((x) => x.is_visible)),
    [allTemplates, showHidden],
  );

  const filtered = useMemo(() => {
    let list = templates;
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((t) => {
        const haystack = [t.name, t.description ?? '', ...(t.tags ?? [])].join(' ').toLowerCase();
        return haystack.includes(q);
      });
    }
    // Category filter — the SAME taxonomy as Covers (cover_categories slugs on
    // album_categories). A model shows under a category when its tags include
    // that slug.
    if (categoryFilter) {
      list = list.filter((t) => (t.album_categories ?? []).includes(categoryFilter));
    }
    // "Récents" — newest uploads first (by created_at); a stable copy so we
    // never mutate the query cache array.
    if (recentFirst) {
      list = [...list].sort((a, b) =>
        (b.created_at ?? '').localeCompare(a.created_at ?? ''));
    }
    return list;
  }, [templates, search, recentFirst, categoryFilter]);

  // Select-all toggles every currently-filtered template.
  const allFilteredSelected = filtered.length > 0 && filtered.every((t) => selectedIds.has(t.id));
  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      if (filtered.length > 0 && filtered.every((t) => prev.has(t.id))) return new Set();
      return new Set(filtered.map((t) => t.id));
    });
  }, [filtered]);

  // Bulk action handler (Task 22)
  const bulk = useCallback(
    async (
      action: Parameters<typeof templatesApi.bulk>[0],
      payload?: Record<string, unknown>,
    ) => {
      const ids = Array.from(selectedIds);
      if (ids.length === 0) return;
      try {
        const res = await templatesApi.bulk(action, ids, payload);
        queryClient.invalidateQueries({ queryKey: ['admin-templates'] });
        if (res.summary.failed > 0) {
          alert(t('{n} échec(s). Les autres ont réussi.', { n: res.summary.failed }));
        }
      } catch (e) {
        alert(t('Erreur : {message}', { message: e instanceof Error ? e.message : t('inconnue') }));
      }
      clearSelection();
    },
    [selectedIds, queryClient, clearSelection, t],
  );

  function confirmDelete() {
    if (confirm(t('Supprimer {n} modèle(s) ?', { n: selectedIds.size }))) bulk('delete');
  }

  return (
    <AdminLayout
      title={t('Modèles de page')}
      subtitle={
        tab === 'themes'
          ? (themeCount
            ? t('{n} modèles du thème {theme}', { n: themeCount.n, theme: t(themeCount.theme) })
            : t('Modèles des thèmes (v2)'))
          : (filtered.length !== 1
            ? t('{n} modèles', { n: filtered.length })
            : t('{n} modèle', { n: filtered.length })) +
            (search.trim() ? t(' pour « {query} »', { query: search }) : '') +
            (!showHidden && hiddenCount > 0 ? t(' · {n} anciens masqués', { n: hiddenCount }) : '')
      }
      actions={tab === 'themes' ? undefined : (
        <div className="flex gap-2 items-center">
          <Button
            variant={selectMode ? 'primary' : 'secondary'}
            onClick={() => setSelectMode((v) => !v)}
          >
            {selectMode ? t('Sélection ({n})', { n: selectedIds.size }) : t('Sélectionner')}
          </Button>
          <Button onClick={() => setUploadOpen(true)}>{t('+ Ajouter un modèle')}</Button>
        </div>
      )}
    >
      <div role="tablist" className="flex gap-1 mb-4 border-b border-gray-200">
        {([['themes', t('Modèles des thèmes (v2)')], ['anciens', t('Anciens modèles')]] as const).map(([k, label]) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={tab === k}
            onClick={() => setTab(k)}
            className={cn(
              '-mb-px px-3 sm:px-4 py-2 text-sm font-medium border-b-2 whitespace-nowrap transition-colors',
              tab === k ? 'border-brand-500 text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-700',
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'themes' ? (
        <>
          {/* The theme bar: tabs, coloris, and the GLOBAL defaults (autosaved — an album's own setting wins). */}
          <ThemeModelsGallery onCount={onThemeCount}
            bars={<GlobalThemeSteppers
              pct={(settingsData?.settings as { theme_model_pct?: number | null } | undefined)?.theme_model_pct ?? null}
              variety={(settingsData?.settings as { theme_model_variety?: number | null } | undefined)?.theme_model_variety ?? null} />} />
        </>
      ) : (
      <>
      {/* ISSUE 3 — how much of an AI-Magic album uses templates. */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm px-4 py-3 mb-4 flex items-center gap-4 flex-wrap">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-800">{t('Utilisation des modèles par la Magie IA')}</p>
          <p className="text-xs text-gray-400">{t('0% = jamais · 100% = chaque page · appliqué aux prochaines générations')}</p>
        </div>
        <div className="flex items-center gap-3 ml-auto">
          <input
            type="range"
            min={0}
            max={100}
            step={5}
            value={effectivePct}
            onChange={(e) => setTplPct(parseInt(e.target.value, 10))}
            onMouseUp={() => savePct(effectivePct)}
            onTouchEnd={() => savePct(effectivePct)}
            className="w-44 accent-[#C9A24A]"
          />
          <span className="text-sm font-bold tabular-nums w-12 text-right text-adm-gold-ink">
            {effectivePct}%{pctSaving ? '…' : ''}
          </span>
        </div>
      </div>

      {selectedIds.size > 0 && (
        <div className="sticky top-0 z-20 bg-white border border-gray-200 rounded-xl shadow-sm px-4 py-2 mb-4 flex items-center gap-3">
          <span className="text-sm font-medium">{t('{n} sélectionné(e)s', { n: selectedIds.size })}</span>
          <div className="ml-auto flex gap-2 flex-wrap items-center">
            <BulkAction label={t('Visibles')} onClick={() => bulk('set_visibility', { value: true })} />
            <BulkAction label={t('Masquer')} onClick={() => bulk('set_visibility', { value: false })} />
            <BulkAction label={t('AI ✓')} onClick={() => bulk('set_ai_eligible', { value: true })} />
            <BulkAction label={t('AI ✗')} onClick={() => bulk('set_ai_eligible', { value: false })} />
            {/* Multi-category bulk (cover taxonomy — what the AI matches on).
                Check SEVERAL categories, apply once — all selected models get
                all checked categories (union with what they had); ∅ clears. */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setBulkCatOpen((v) => !v)}
                className="px-2 py-1 text-sm border border-gray-200 rounded bg-white hover:bg-gray-50"
              >
                {t('+ Catégories…')}{bulkCats.size > 0 ? ` (${bulkCats.size})` : ''}
              </button>
              {bulkCatOpen && (
                <div className="absolute right-0 top-full mt-1 z-30 bg-white border border-gray-200 rounded-xl shadow-lg p-2 w-56">
                  <div className="max-h-56 overflow-y-auto">
                    {coverCategories.map((c) => (
                      <label key={c.slug} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer text-sm">
                        <input
                          type="checkbox"
                          checked={bulkCats.has(c.slug)}
                          onChange={() => setBulkCats((prev) => {
                            const next = new Set(prev);
                            if (next.has(c.slug)) next.delete(c.slug); else next.add(c.slug);
                            return next;
                          })}
                        />
                        <span>{c.icon ? `${c.icon} ` : ''}{c.label_fr}</span>
                      </label>
                    ))}
                  </div>
                  <button
                    type="button"
                    disabled={bulkCats.size === 0}
                    onClick={() => {
                      bulk('add_categories', { values: Array.from(bulkCats) });
                      setBulkCats(new Set());
                      setBulkCatOpen(false);
                    }}
                    className="mt-2 w-full px-3 py-1.5 text-sm font-semibold rounded-lg text-white disabled:opacity-40"
                    style={{ background: '#C9A24A' }}
                  >
                    {t('Appliquer')}
                  </button>
                </div>
              )}
            </div>
            <BulkAction label={t('Catégories ∅')} onClick={() => bulk('clear_categories')} />
            <BulkAction label={t('Supprimer')} danger onClick={confirmDelete} />
            <button
              onClick={clearSelection}
              className="text-gray-500 hover:text-gray-700 text-sm ml-2"
            >
              {t('Annuler')}
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-3 mb-4 items-center">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('Rechercher un modèle (nom, tag, description)…')}
          className="flex-1 min-w-[12rem] px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white"
        />
        <button
          type="button"
          onClick={() => setRecentFirst((v) => !v)}
          title={t('Trier par date d\'ajout (plus récents en premier)')}
          className={cn(
            'px-3.5 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-colors border',
            recentFirst ? 'bg-brand-500 text-white border-brand-500' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50',
          )}
        >
          {t('🕑 Récents')}
        </button>
        {hiddenCount > 0 && (
          <button
            type="button"
            onClick={() => setShowHidden((v) => !v)}
            aria-pressed={showHidden}
            className={cn(
              'px-3.5 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-colors border',
              showHidden ? 'bg-gray-800 text-white border-gray-800' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50',
            )}
          >
            {showHidden ? t('Masquer les anciens modèles') : t('Afficher les anciens modèles ({n})', { n: hiddenCount })}
          </button>
        )}
        {selectMode && (
          <button
            type="button"
            onClick={toggleSelectAll}
            className="px-3.5 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-colors border bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
          >
            {allFilteredSelected ? t('Tout désélectionner') : t('Tout sélectionner ({n})', { n: filtered.length })}
          </button>
        )}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-2 mb-5 flex gap-1 overflow-x-auto">
        <button
          onClick={() => setCategoryFilter('')}
          className={cn(
            'px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors',
            !categoryFilter ? 'bg-brand-500 text-white' : 'text-gray-600 hover:bg-gray-50',
          )}
        >
          {t('Tous')}
        </button>
        {coverCategories.map((c) => (
          <button
            key={c.slug}
            onClick={() => setCategoryFilter(c.slug)}
            className={cn(
              'px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors',
              categoryFilter === c.slug ? 'bg-brand-500 text-white' : 'text-gray-600 hover:bg-gray-50',
            )}
          >
            {c.icon ? `${c.icon} ` : ''}{c.label_fr}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="aspect-[210/297] bg-white rounded-xl border border-gray-100 animate-pulse" />
          ))}
        </div>
      ) : filtered.length === 0 && !showHidden && hiddenCount > 0 && !search.trim() && !categoryFilter ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
          <div className="text-5xl mb-3">📦</div>
          <h3 className="font-semibold text-gray-900 mb-1">{t('Aucun modèle visible')}</h3>
          <p className="text-gray-500 text-sm mb-4">
            {t('Les {n} anciens modèles sont retirés : ils ne sont plus proposés, mais restent pour les albums qui les utilisent.', { n: hiddenCount })}
          </p>
          <div className="flex gap-2 justify-center flex-wrap">
            <Button variant="secondary" onClick={() => setShowHidden(true)}>{t('Afficher les anciens modèles ({n})', { n: hiddenCount })}</Button>
            <Button onClick={() => setTab('themes')}>{t('Voir les modèles des thèmes')}</Button>
          </div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
          <div className="text-5xl mb-3">📐</div>
          <h3 className="font-semibold text-gray-900 mb-1">{t('Aucun modèle')}</h3>
          <p className="text-gray-500 text-sm mb-4">{t('Téléversez votre premier modèle pour commencer.')}</p>
          <Button onClick={() => setUploadOpen(true)}>{t('+ Ajouter un modèle')}</Button>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {filtered.map((t) => (
            <TemplateCard
              key={t.id}
              template={t}
              categoryMap={categoryMap}
              selectMode={selectMode}
              selected={selectedIds.has(t.id)}
              onClick={() => {
                if (selectMode) toggleSelected(t.id);
                else setEditing(t);
              }}
            />
          ))}
        </div>
      )}

      </>
      )}

      {uploadOpen && (
        <UploadTemplateDialog isOpen={uploadOpen} onClose={() => setUploadOpen(false)} />
      )}

      {editing && (
        <TemplateEditDialog template={editing} onClose={() => setEditing(null)} />
      )}
    </AdminLayout>
  );
}

function BulkAction({
  label,
  onClick,
  danger,
}: {
  label: string;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1 text-sm rounded ${
        danger
          ? 'bg-red-50 text-red-700 hover:bg-red-100'
          : 'bg-gray-50 hover:bg-gray-100'
      }`}
    >
      {label}
    </button>
  );
}

function TemplateCard({
  template,
  categoryMap,
  onClick,
  selectMode,
  selected,
}: {
  template: LayoutTemplate;
  categoryMap: Record<string, CoverCategory>;
  onClick: () => void;
  selectMode: boolean;
  selected: boolean;
}) {
  const { t } = useT();

  // Unified taxonomy first; when album_categories is empty, fall back to the
  // legacy single English enum mapped to its cover-category slug.
  const categorySlugs =
    template.album_categories && template.album_categories.length > 0
      ? template.album_categories
      : template.album_category
        ? [LEGACY_ALBUM_CATEGORY_TO_SLUG[template.album_category]].filter(Boolean)
        : [];
  const categoryLabel = categorySlugs.length
    ? categorySlugs
        .map((slug) => {
          const c = categoryMap[slug];
          return c ? `${c.icon ? `${c.icon} ` : ''}${c.label_fr}` : slug;
        })
        .join(', ')
    : t('Toutes catégories');
  return (
    <button
      onClick={onClick}
      className={cn(
        'group relative bg-white rounded-xl border overflow-hidden hover:shadow-lg transition-shadow text-left',
        selected ? 'border-brand-500 ring-2 ring-brand-500' : 'border-gray-100',
      )}
    >
      {selectMode && (
        <div
          onClick={(e) => {
            e.stopPropagation();
            onClick();
          }}
          style={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 5,
            width: 22,
            height: 22,
            borderRadius: 4,
            border: '2px solid #fff',
            background: selected ? '#C9A24A' : 'rgba(255,255,255,0.6)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: 14,
            lineHeight: 1,
          }}
        >
          {selected ? '✓' : ''}
        </div>
      )}
      <div
        className="aspect-[210/297] relative"
        data-adm-media
        style={{
          backgroundColor: template.background_color ?? '#f3f4f6',
          backgroundImage: 'repeating-conic-gradient(#e5e7eb 0% 25%, transparent 0% 50%)',
          backgroundSize: '16px 16px',
        }}
      >
        {template.background_url && (
          <img
            src={template.background_url}
            alt={template.name}
            className="absolute inset-0 w-full h-full object-contain"
            loading="lazy"
          />
        )}
        {!template.is_visible && !selectMode && (
          <div className="absolute top-2 right-2 bg-gray-800 text-white text-xs px-2 py-0.5 rounded-full">
            {t('Masqué')}
          </div>
        )}
        {template.is_ai_eligible && (
          <div className="absolute top-2 left-2 bg-brand-500 text-white text-xs px-2 py-0.5 rounded-full">
            {t('IA')}
          </div>
        )}
      </div>
      <div className="p-3">
        <p className="font-medium text-gray-900 text-sm truncate">{template.name}</p>
        <p className="text-xs text-gray-500 truncate">
          {categoryLabel}
          {' · '}
          {template.slot_count === 1
            ? t('{n} zone', { n: template.slot_count })
            : t('{n} zones', { n: template.slot_count })}
        </p>
      </div>
    </button>
  );
}
